import {
  ContainerKind,
  DSAttendanceStatus,
  type ActivityRiskFlag
} from '@/constants/ds-attendance.constants'
import type {
  DSAttendanceContainer,
  DSAttendanceEffectiveSummary
} from '@/types/ds-attendance.types'

/**
 * O item que os cards da fila recebem. Movido do `Index.vue` sem mudança de forma, para que
 * a fila de hoje e as visões novas (Lista/Tabela/Kanban) mapeiem o container do mesmo jeito.
 */
export interface AttendanceListItem {
  attendanceId: number
  date: string
  submissionDate: string
  unit: string
  schedule: string
  shifts: string
  shiftsCount: number
  status: DSAttendanceStatus
  /** Apontamentos 3 e 4 — decidem a fila de exceções e a elegibilidade do lote. */
  hasDivergentEntries?: boolean
  bulkApproveEnabled?: boolean
  /** Pacote 2026-09-10 — indicativo de pagamento do registro (pílulas do card). */
  hasAntecipe: boolean
  hasInCash: boolean
  /** Vaga/plantão alterada depois do início da auditoria (indicador âmbar no card). */
  stationChangedAfterAudit?: boolean
  /** Fase 1 do fluxo de auditoria — `false` acende o chip cinza "Sem escala prevista". */
  hasScheduleExpected?: boolean
  /** Fase 2 — chip âmbar do motivo da revisão manual e chip verde do aprovado sozinho. */
  manualReviewReason?: string | null
  approvalMode?: string | null
  /**
   * F2 — campos do Registro de Atividade. Todos opcionais: `kind` ausente é backend
   * anterior à F2 e o card renderiza a variante de folha, idêntica à de hoje.
   */
  kind?: string | null
  activityType?: string | null
  activityTypeLabel?: string | null
  activityDescription?: string | null
  coveredLegs?: string | null
  declaredCheckin?: string | null
  declaredCheckout?: string | null
  doctorName?: string | null
  doctorCrm?: string | null
  doctorCrmNumber?: string | null
  doctorCrmUf?: string | null
  attachmentCount?: number | null
  submittedByName?: string | null
  activityRiskFlags?: Partial<Record<ActivityRiskFlag, boolean>> | null
  effectiveSummary?: DSAttendanceEffectiveSummary | null
  afterClosure?: boolean
  legacyMigrated?: boolean
  /** F3 — campos da solicitação automática; preenchidos só nos cards de cobrança. */
  requestedLeg?: string | null
  requestChannel?: string | null
  expectedSubmissionAt?: string | null
  createdAt?: string | null
  /** Offset do plantão (`-03:00`). O backend já o serve (`buildActivityEntryFields`). */
  timezone?: string | null
  /** §6.5 item 3 — vencimento decidido pelo servidor; `null` = backend sem o campo. */
  isOverdue?: boolean | null
}

/**
 * F2 — campos do Registro de Atividade do item da listagem. Em função à parte porque o
 * `map` da fila já estourava o teto de complexidade do lint com os filtros de hoje; todos
 * são repassados crus (ausente = backend anterior à F2, e o card sabe o que fazer).
 */
function orNull<T>(value: T | null | undefined): T | null {
  return value ?? null
}

/**
 * O backend manda o CRM composto E as duas metades (B17 §1.2). O card usa o composto; se
 * um dia só as metades vierem, montá-las aqui é o que evita o card perder o CRM em
 * silêncio. Sem UF, o número sozinho já identifica o médico e vale mais que nada.
 */
function composeCrm(number?: string | null, uf?: string | null): string | null {
  if (!number) return null
  return uf ? `${number}/${uf}` : number
}

function mapActivityFields(item: DSAttendanceContainer) {
  return {
    kind: orNull(item.kind as string | undefined),
    activityType: orNull(item.activityType as string | undefined),
    activityTypeLabel: orNull(item.activityTypeLabel),
    activityDescription: orNull(item.activityDescription),
    coveredLegs: orNull(item.coveredLegs as string | undefined),
    declaredCheckin: orNull(item.declaredCheckin),
    declaredCheckout: orNull(item.declaredCheckout),
    doctorName: orNull(item.doctorName),
    doctorCrm: orNull(item.doctorCrm) ?? composeCrm(item.doctorCrmNumber, item.doctorCrmUf),
    doctorCrmNumber: orNull(item.doctorCrmNumber),
    doctorCrmUf: orNull(item.doctorCrmUf),
    attachmentCount: orNull(item.attachmentCount),
    submittedByName: orNull(item.submittedByName),
    activityRiskFlags: orNull(item.activityRiskFlags),
    effectiveSummary: orNull(item.effectiveSummary),
    // F3 — só a solicitação automática traz estes quatro; no resto da fila são `null`.
    requestedLeg: orNull(item.requestedLeg as string | undefined),
    requestChannel: orNull(item.requestChannel as string | undefined),
    expectedSubmissionAt: orNull(item.expectedSubmissionAt),
    createdAt: orNull(item.createdAt),
    // §6.5 — sem isto o card caía no `DEFAULT_UTC_OFFSET` e mostrava -03:00 para plantão
    // em -04:00/-05:00.
    timezone: orNull(item.timezone),
    // §6.5 item 3 — o `by-company` já calcula `isOverdue` (`resolveIsOverdue`); `null` =
    // backend sem o campo, e o card cai no cálculo local de hoje.
    isOverdue: typeof item.isOverdue === 'boolean' ? item.isOverdue : null,
    afterClosure: item.afterClosure === true,
    legacyMigrated: item.legacyMigrated === true
  }
}

/** Container do `by-company` → item que os cards da fila recebem. */
export function toAttendanceListItem(item: DSAttendanceContainer): AttendanceListItem {
  const shifts = item.shifts || []
  const count = shifts.length

  return {
    attendanceId: item.id,
    date: item.referenceDate,
    submissionDate: item.evidenceUploadedAt ?? '',
    unit: item.healthUnit?.name ?? 'Unidade não informada',
    schedule: item.schedule?.name ?? 'Escala não informada',
    shifts:
      count > 0
        ? shifts.map((shift: { id: number; name: string }) => shift?.name).join(', ')
        : 'Sem turno',
    shiftsCount: count,
    status: item.status,
    hasDivergentEntries: item.hasDivergentEntries,
    stationChangedAfterAudit: item.stationChangedAfterAudit === true,
    bulkApproveEnabled: item.bulkApproveEnabled,
    hasAntecipe: item.hasAntecipe === true,
    hasInCash: item.hasInCash === true,
    // Repassado cru (sem `=== true`): `undefined` é backend antigo e não vira chip.
    hasScheduleExpected: item.hasScheduleExpected,
    manualReviewReason: item.manualReviewReason ?? null,
    approvalMode: (item.approvalMode as string | null) ?? null,
    ...mapActivityFields(item)
  }
}

/**
 * Um item é uma COBRANÇA (e não um registro) quando é Atividade, ainda não foi enviado e traz
 * a perna que a política pediu. Os três juntos, de propósito: `PENDING_SUBMISSION` sozinho
 * também descreve a pendência de folha criada pelo job da escala, que não é solicitação.
 *
 * Backend anterior à F3 nunca manda `requestedLeg` — então nada muda de lugar na fila.
 */
export function isRequestListItem(
  item: Pick<AttendanceListItem, 'kind' | 'status' | 'requestedLeg'>
): boolean {
  return (
    item.kind === ContainerKind.ATIVIDADE &&
    item.status === DSAttendanceStatus.PENDING_SUBMISSION &&
    !!item.requestedLeg
  )
}
